// Helper functions for bill printing
export const formatDate = (dateString) => {
  if (!dateString) return '';
  // Remove leading quote if present (from your data format '11/12/2025)
  const cleanDate = dateString.replace(/^'/, '');
  return cleanDate;
};

export const generateTransactionBillHTML = (transaction, customers) => {
  const customer = customers.find(c => c.id === transaction.customer) || {};
  const isGst = transaction.transactionDetails?.gstApplicable;
  const gstType = transaction.transactionDetails?.gstType || 'cgst_sgst';
  
  // Use the existing generateBillHTML function or create a new one
  const billData = {
    type: isGst ? 'gst' : 'non-gst',
    data: {
      transactionDetails: {
        billNo: transaction.transactionDetails?.billNo || 1,
        gstApplicable: isGst,
        gstType: gstType
      },
      date: transaction.date,
      calculatedTotals: {
        subtotal: transaction.calculatedTotals?.subtotal || 0
      },
      paymentDetails: {
        cash: transaction.paymentDetails?.cash || 0,
        online: transaction.paymentDetails?.online || 0,
        cheque: transaction.paymentDetails?.cheque || 0,
        other: transaction.paymentDetails?.other || 0,
        advancePayment: {
          enabled: transaction.paymentDetails?.advancePayment?.enabled || false,
          total: transaction.paymentDetails?.advancePayment?.total || 0,
          breakup: transaction.paymentDetails?.advancePayment?.breakup || {}
        },
        discount: transaction.paymentDetails?.discount || 0
      },
      metaDetails: {
        handledBy: transaction.metaDetails?.handledBy || '',
        gstNumber: transaction.metaDetails?.gstNumber || '',
        panNumber: transaction.metaDetails?.panNumber || ''
      },
      summary: {
        grandTotal: transaction.summary?.grandTotal || 0,
        pendingAmount: transaction.summary?.pendingAmount || 0,
        cgst: transaction.summary?.cgst || 0,
        sgst: transaction.summary?.sgst || 0,
        igst: transaction.summary?.igst || 0
      },
      gstType: gstType // Pass at data level too
    },
    items: transaction.items || [],
    payments: transaction.paymentDetails || {},
    customer: customer,
    advanceData: transaction.paymentDetails?.advancePayment?.breakup || {},
    gstType: gstType // Top level access
  };
  
  return generateBillHTML(billData);
};

export const parseItemObject = (itemObject) => {
  if (!itemObject) return {};
  try {
    if (typeof itemObject === 'string') {
      return JSON.parse(decodeURIComponent(itemObject));
    }
    return itemObject;
  } catch (error) {
    console.warn("Could not parse item object:", error);
    return {};
  }
};

export const getCarat = (melting) => {
  if (!melting) return "";
  switch(String(melting)) {
    case "92": return "22ct";
    case "75":
    case "76": return "18ct";
    case "58.5": return "14ct";
    case "50": return "12ct";
    case "999": return "24ct";
    default: return "";
  }
};

// Indian Number to Words Conversion
export const numberToWords = (num) => {
  if (typeof num !== 'number') num = parseFloat(num) || 0;
  num = Math.floor(num); // Convert to integer
  
  if (num === 0) return 'Zero Rupees Only';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 
                'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 
                'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const convertLessThanOneThousand = (n) => {
    if (n === 0) return '';
    
    let result = '';
    
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    
    if (n > 0) {
      result += ones[n] + ' ';
    }
    
    return result.trim();
  };
  
  const convertToIndianWords = (n) => {
    if (n === 0) return '';
    
    let crore = Math.floor(n / 10000000);
    n %= 10000000;
    
    let lakh = Math.floor(n / 100000);
    n %= 100000;
    
    let thousand = Math.floor(n / 1000);
    n %= 1000;
    
    let hundred = n;
    
    let result = '';
    
    if (crore > 0) {
      result += convertLessThanOneThousand(crore) + ' Crore ';
    }
    
    if (lakh > 0) {
      result += convertLessThanOneThousand(lakh) + ' Lakh ';
    }
    
    if (thousand > 0) {
      result += convertLessThanOneThousand(thousand) + ' Thousand ';
    }
    
    if (hundred > 0) {
      result += convertLessThanOneThousand(hundred);
    }
    
    return result.trim();
  };
  
  const words = convertToIndianWords(num);
  return words ? words + ' Rupees Only' : 'Zero Rupees Only';
};

// Generate Non-GST Bill HTML
const generateNonGSTBill = (data, items, payments, customer, advanceData) => {
  let itemsHTML = '';
  let total = 0;
  
  items.forEach((item, index) => {
    const itemObj = parseItemObject(item.itemObject);
    const amount = parseFloat(item.total) || 0;
    total += amount;
    
    itemsHTML += `
      <tr>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${index + 1}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: left;">${item.name}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${itemObj.grossWeight || 'N/A'}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${itemObj.lessWeight || 'N/A'}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${item.weight}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${item.metalRate || item.rate}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${item.wastage}%</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: right;">${amount.toFixed(2)}</td>
      </tr>
    `;
  });
  
  // Calculate filler rows
  const currentRowCount = items.length;
  const minTotalRows = 13;
  const rowsNeeded = minTotalRows - (currentRowCount + 1);
  const fillerHeight = Math.max(0, rowsNeeded * 25);
  
  if (fillerHeight > 0) {
    itemsHTML += `
      <tr style="height: ${fillerHeight}px;">
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
      </tr>`;
  }
  
  itemsHTML += `
    <tr>
      <td colspan="7" style="border: 1px solid #000; padding: 4px; text-align: right;"><b>Grand Total &nbsp;</b></td>
      <td style="border: 1px solid #000; padding: 4px; text-align: right;"><b>${total.toFixed(2)}</b></td>
    </tr>`;
  
  const chargeableAmt = (parseFloat(payments?.cash) || 0) + 
                       (parseFloat(payments?.online) || 0) + 
                       (parseFloat(payments?.cheque) || 0) + 
                       (parseFloat(payments?.other) || 0) + 
                       (parseFloat(payments?.advancePayment?.total) || 0) + 
                       (parseFloat(data?.summary?.pendingAmount) || 0);
  
  return `
    <div id="billContentWithoutGst" style="font-family: 'Times New Roman', serif; padding: 15px; width: 750px; margin: 0 auto; background: white; border: 1px solid black">
      <!-- Watermark -->
      <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); opacity: 0.1; z-index: 9999; pointer-events: none;">
        <img src="/assets/images/logo.jpeg" alt="Logo" style="width: 400px; height: 400px; object-fit: 'contain'">
      </div>
      
      <div style="width: 100%; background-color: #f9f1b0; text-align: center; border-bottom: 3px solid #d4b44a; padding: 10px 0;">
        <div style="color: #b01c1c; font-weight: bold; font-size: 16px; margin-bottom: 10px;">|| श्री गणेशाय नमः ||</div>
        <div style="display: flex; align-items: center; justify-content: space-around; padding: 0 40px;">
          <div style="flex: 1;">
            <img src="/assets/images/logo.jpeg" alt="Supriya Jewellers Logo" style="height: 70px; width: auto; border-radius: 35px;">
          </div>
          <div style="flex: 3; text-align: center;">
            <h1 style="margin: 0; font-size: 30px; font-weight: 900; color: #c00;">SUPRIYA JEWELLERS</h1>
            <h3 style="margin: 5px 0 0; font-weight: 600;">Specialist in Gold & Silver Ornaments</h3>
          </div>
          <div style="flex: 1; display: flex; justify-content: flex-end;">
            <p><strong>Mob.:</strong>93727 98142</p>
          </div>
        </div>
        <div style="font-size: 13px; color: #333; padding-top: 5px;">
          Shop No. 6 & 7, Shrinath Building, B.P Road, Near Goddev Naka, Bhayandar (East), Dist. Thane - 401105
        </div>
      </div>
      <hr style="border: 1px solid #000; margin: 10px 0;">
      
      <h3 style="text-align:center; margin:5px 0;">CASH MEMO</h3>

      <div style="display:flex; justify-content:space-between; font-size:14px; margin-bottom: 15px;">
        <div>
          <p><strong>Bill No:</strong> <span>${data.transactionDetails?.billNo || 1}</span></p>
          <p><strong>Name:</strong> <span>${customer?.name || ''}</span></p>
          <p><strong>Address:</strong> <span>${customer?.address || ''}</span></p>
        </div>
        <div style="text-align: right;">
          <p><strong>Date:</strong> <span>${formatDate(data.date)}</span></p>
          <p><strong>Mob No.:</strong> <span>${customer?.contact || ''}</span></p>
          <p><strong>Seller name:</strong> <span>${data.metaDetails?.handledBy || ''}</span></p>
        </div>
      </div>
      
      <table border="1" cellspacing="0" cellpadding="4" width="100%" style="font-size:13px; border-collapse: collapse; text-align:center; margin-top:5px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Sr. No.</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Description</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">GW (gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">LW (gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">NW (gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; min-width: 60px;">Rate(p/gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Making</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHTML}
        </tbody>
      </table>

      <div style="margin: 15px 0; border-top: 2px solid #999; padding-top: 10px; font-size: 13px;">
        <strong>Amount Chargeable (in words):</strong><br>
        <span>INR ${numberToWords(chargeableAmt).toUpperCase()}</span>
      </div>

      <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
        <thead>
          <tr>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Cash</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Online</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Cheque</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Old Gold Value (₹)</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Discount</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Total Balance</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Total Received</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.cash || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.online || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.cheque || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.other || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.advancePayment?.total || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${parseFloat(payments?.discount || 0).toFixed(2)}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${parseFloat(data?.summary?.pendingAmount || 0).toFixed(2)}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${(
              (parseFloat(payments?.cash || 0) + 
               parseFloat(payments?.advancePayment?.total || 0) + 
               parseFloat(payments?.online || 0) + 
               parseFloat(payments?.cheque || 0) + 
               parseFloat(payments?.other || 0)
              ).toFixed(2))
            }</td>
          </tr>
        </tbody>
      </table>
      
      ${payments?.advancePayment?.enabled ? `
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
          <thead>
            <tr>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Cash</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Online</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Old Gold (₹)</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Old Silver</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Other</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Total Advance</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.cash || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.online || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.oldGold || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.oldSilver || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.custom || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.advancePayment?.total || 0}</td>
            </tr>
          </tbody>
        </table>
      ` : ''}

      <div style="margin-top: 20px;">
        <p style="font-size:12px; margin-top:8px;">
          10% Deduction in Exchange. For KDM 20% Deduction in Cash Transaction. <br>
          Old Ornaments / Hallmark Jewellery Exchange Weight to Weight. <br>
          10% Deduction in Cash Transaction for Hallmark Jewellery Melting. <br>
        </p>

        <div style="display:flex; justify-content:space-between; font-size:13px; margin-top:15px;">
          <div>
            <p><strong>Customer's Signature</strong></p><br>
            <div style="margin-top: 5px;">___________________</div>
          </div>
          <div>
            <div style="text-align: right; display: flex; flex-direction: column; padding: 10px 10px 15px 10px; width: fit-content; margin-left: auto;">
              <p>for <strong>SUPRIYA JEWELLERS</strong></p><br>
              <span>__________________________</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

// Generate GST Bill HTML with IGST Support
const generateGSTBill = (data, items, payments, customer, advanceData) => {
  // ✅ Get GST type from data - check multiple locations
  const gstType = data?.transactionDetails?.gstType || data?.gstType || 'cgst_sgst';
  let itemsHTML = '';
  let total = 0;
  
  items.forEach((item, index) => {
    const itemObj = parseItemObject(item.itemObject);
    const metalType = itemObj.metal || '';
    const hsnCode = metalType === "Gold" ? "711319" : "711319";
    const amount = parseFloat(item.total) || 0;
    total += amount;
    
    itemsHTML += `
      <tr>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${index + 1}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: left;">${item.name}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${item.huid || ''}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${hsnCode}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${itemObj.grossWeight || 'N/A'}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${itemObj.lessWeight || 'N/A'}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${item.weight}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${item.metalRate || item.rate}</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: center;">${item.wastage}%</td>
        <td style="border: 1px solid #000; padding: 4px; text-align: right;">${amount.toFixed(2)}</td>
      </tr>
    `;
  });
  
  // Calculate filler rows
  const currentRowCount = items.length;
  const minTotalRows = 9;
  const rowsNeeded = minTotalRows - (currentRowCount + 1);
  const fillerHeight = Math.max(0, rowsNeeded * 25);
  
  if (fillerHeight > 0) {
    itemsHTML += `
      <tr style="height: ${fillerHeight}px;">
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
      </tr>`;
  }
  
  // ✅ Calculate taxes based on GST type
  const cgst = total * 0.015;
  const sgst = total * 0.015;
  const igst = total * 0.03;
  const grandTotal = gstType === 'igst' ? total + igst : total + cgst + sgst;
  
  itemsHTML += `
    <tr>
      <td colspan="9" style="border: 1px solid #000; padding: 4px; text-align: center;"><b>Total</b></td>
      <td style="border: 1px solid #000; padding: 4px; text-align: right;"><b>${total.toFixed(2)}</b></td>
    </tr>`;
  
  // ✅ Show GST based on type
  if (gstType === 'igst') {
    itemsHTML += `
      <tr>
        <td colspan="9" style="border: 1px solid #000; padding: 4px; text-align: center;"><b>IGST (3%)</b></td>
        <td style="border: 1px solid #000; padding: 4px; text-align: right;"><b>${igst.toFixed(2)}</b></td>
      </tr>`;
  } else {
    itemsHTML += `
      <tr>
        <td colspan="9" style="border: 1px solid #000; padding: 4px; text-align: center;"><b>CGST (1.5%)</b></td>
        <td style="border: 1px solid #000; padding: 4px; text-align: right;"><b>${cgst.toFixed(2)}</b></td>
      </tr>
      <tr>
        <td colspan="9" style="border: 1px solid #000; padding: 4px; text-align: center;"><b>SGST (1.5%)</b></td>
        <td style="border: 1px solid #000; padding: 4px; text-align: right;"><b>${sgst.toFixed(2)}</b></td>
      </tr>`;
  }
  
  itemsHTML += `
    <tr>
      <td colspan="9" style="border: 1px solid #000; padding: 4px; text-align: center;"><b>Grand Total</b></td>
      <td style="border: 1px solid #000; padding: 4px; text-align: right;"><b>${grandTotal.toFixed(2)}</b></td>
    </tr>`;
  
  const chargeableAmt = (parseFloat(payments?.cash) || 0) + 
                       (parseFloat(payments?.online) || 0) + 
                       (parseFloat(payments?.cheque) || 0) + 
                       (parseFloat(payments?.other) || 0) + 
                       (parseFloat(payments?.advancePayment?.total) || 0) + 
                       (parseFloat(data?.summary?.pendingAmount) || 0);
  
  // ✅ Update title based on GST type
  const invoiceTitle = gstType === 'igst' ? 'TAX INVOICE' : 'TAX INVOICE';
  
  return `
    <div id="billContent" style="font-family: 'Times New Roman', serif; padding: 15px; width: 750px; margin: 0 auto; background: white; border: 1px solid black">
      <!-- Watermark -->
      <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); opacity: 0.1; z-index: 9999; pointer-events: none;">
        <img src="../assets/images/logo.jpeg" alt="Logo" style="width: 400px; height: 400px; object-fit: 'contain'">
      </div>
      
      <div style="width: 100%; background-color: #f9f1b0; text-align: center; border-bottom: 3px solid #d4b44a; padding: 10px 0;">
        <div style="color: #b01c1c; font-weight: bold; font-size: 16px; margin-bottom: 10px;">|| श्री गणेशाय नमः ||</div>
        <div style="display: flex; align-items: center; justify-content: space-around; padding: 0 40px;">
          <div style="flex: 1;">
            <img src="../assets/images/logo.jpeg" alt="Supriya Jewellers Logo" style="height: 70px; width: auto; border-radius: 35px;">
          </div>
          <div style="flex: 3; text-align: center;">
            <h1 style="margin: 0; font-size: 30px; font-weight: 900; color: #c00;">SUPRIYA JEWELLERS</h1>
            <h3 style="margin: 5px 0 0; font-weight: 600;">Specialist in Gold & Silver Ornaments</h3>
          </div>
          <div style="flex: 1; display: flex; justify-content: flex-end;">
            <p><strong>Mob.:</strong>93727 98142</p>
          </div>
        </div>
        <div style="font-size: 13px; color: #333; padding-top: 5px;">
          Shop No. 6 & 7, Shrinath Building, B.P Road, Near Goddev Naka, Bhayandar (East), Dist. Thane - 401105
        </div>
        <h3 style="color: red; margin: 5px 0;">GST Number: 27AAWFS7496N1ZR</h3>
      </div>
      <hr style="border: 1px solid #000; margin: 10px 0;">
      
      <h3 style="text-align:center; margin:5px 0;">${invoiceTitle}</h3>

      <div style="display:flex; justify-content:space-between; font-size:14px; margin-bottom: 15px;">
        <div>
          <p><strong>Bill No:</strong> <span>${data.transactionDetails?.billNo || 1}</span></p>
          <p><strong>Name:</strong> <span>${customer?.name || ''}</span></p>
          <p><strong>Address:</strong> <span>${customer?.address || ''}</span></p>
          ${data.metaDetails?.gstNumber ? `
            <p id="customer-gst-p"><strong>Customer GST No.:</strong> <span>${data.metaDetails.gstNumber}</span></p>
          ` : ''}
        </div>
        <div style="text-align: right;">
          <p><strong>Date:</strong> <span>${formatDate(data.date)}</span></p>
          <p><strong>Tel:</strong> <span>${customer?.contact || ''}</span></p>
          <p><strong>Seller Name:</strong> <span>${data.metaDetails?.handledBy || ''}</span></p>
          ${data.metaDetails?.panNumber ? `
            <p id="customer-pan-p"><strong>Customer PAN No.:</strong> <span>${data.metaDetails.panNumber}</span></p>
          ` : ''}
        </div>
      </div>
      
      <table border="1" cellspacing="0" cellpadding="4" width="100%" style="font-size:13px; border-collapse: collapse; text-align:center; margin-top:5px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Sr. No.</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Description</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">HUID No.</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">HSN Code</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">GW (gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">LW (gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">NW (gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Rate(p/gm)</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Making</th>
            <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3;">Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHTML}
        </tbody>
      </table>

      <div style="margin: 15px 0; border-top: 2px solid #999; padding-top: 10px; font-size: 13px;">
        <strong>Amount Chargeable (in words):</strong><br>
        <span>INR ${numberToWords(chargeableAmt).toUpperCase()}</span>
      </div>

      <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
        <thead>
          <tr>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Cash</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Online</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Cheque</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Old Gold</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Discount</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Balance</th>
            <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.cash || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.online || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.cheque || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.other || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.advancePayment?.total || 0}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${parseFloat(payments?.discount || 0).toFixed(2)}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${parseFloat(data?.summary?.pendingAmount || 0).toFixed(2)}</td>
            <td style="border: 1px solid #999; padding: 6px; text-align: center;">${(
              (parseFloat(payments?.cash || 0) + 
               parseFloat(payments?.advancePayment?.total || 0) + 
               parseFloat(payments?.online || 0) + 
               parseFloat(payments?.cheque || 0) + 
               parseFloat(payments?.other || 0)
              ).toFixed(2))
            }</td>
          </tr>
        </tbody>
      </table>
      
      ${payments?.advancePayment?.enabled ? `
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px;">
          <thead>
            <tr>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Cash</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Online</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Old Gold</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Advance Old Silver</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Other</th>
              <th style="border: 1px solid #999; padding: 6px; text-align: center; background-color: #f3f3f3;">Total Advance</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.cash || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.online || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.oldGold || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.oldSilver || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${advanceData?.custom || 0}</td>
              <td style="border: 1px solid #999; padding: 6px; text-align: center;">${payments?.advancePayment?.total || 0}</td>
            </tr>
          </tbody>
        </table>
      ` : ''}

      <div style="display: flex; justify-content: space-between; margin-top: 20px; border-top: 2px solid #999; padding-top: 10px; font-size: 13px;">
        <div style="width: 50%;">
          <p>
            10% Deduction in Exchange. For KDM 20% Deduction in Cash Transaction.<br>
            Old Ornaments / Hallmark Jewellery Exchange Weight to Weight.<br>
            10% Deduction in Cash Transaction for Hallmark Jewellery Melting.<br>
          </p>
          <p style="margin-top: 20px;"><strong>Customer's Signature</strong></p>
          <div style="margin-top: 5px;">__________________________</div>
        </div>
        <div style="width: 45%; border-left: 1px solid #999; padding-left: 10px;">
          <table style="width: 100%; border-collapse: collapse; line-height: 0.8; font-size: 13px;">
            <tbody>
              <tr>
                <td style="padding: 2px;"><b>A/c Holder's Name</b></td>
                <td style="padding: 2px;">Supriya Jewellers</td>
              </tr>
              <tr>
                <td style="padding: 2px;"><b>Bank Name</b></td>
                <td style="padding: 2px;">Karnataka Bank Ltd.</td>
              </tr>
              <tr>
                <td style="padding: 2px;"><b>A/c No.</b></td>
                <td style="padding: 2px;">1062000100004601</td>
              </tr>
              <tr>
                <td style="padding: 2px;"><b>Branch</b></td>
                <td style="padding: 2px;">Bhayandar (East)</td>
              </tr>
              <tr>
                <td style="padding: 2px;"><b>IFSC Code</b></td>
                <td style="padding: 2px;">KARB0000106</td>
              </tr>
              <tr>
                <td style="padding: 2px;"><b>PAN Number</b></td>
                <td style="padding: 2px;">AAWFS7496N</td>
              </tr>
              <tr>
                <td style="padding: 2px;"><b>Mobile Number</b></td>
                <td style="padding: 2px;">98675 83808</td>
              </tr>
            </tbody>
          </table>
          <div style="text-align: right; margin-top: 10px;">
            <p><b>Seller's Signature</b></p>
            <span>________________</span>
          </div>
        </div>
      </div>
    </div>
  `;
};

// Generate bill HTML based on type
export const generateBillHTML = (billData) => {
  const { type, data, items, payments, customer, advanceData } = billData;
  
  return type === 'gst' 
    ? generateGSTBill(data, items, payments, customer, advanceData)
    : generateNonGSTBill(data, items, payments, customer, advanceData);
};

// Get CSS styles for printing
export const getBillStyles = () => `
/* Base styles for bill printing */
@media print {
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
  }
  
  body * {
    visibility: hidden !important;
  }
  
  #billContent, #billContentWithoutGst {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 10px 10px !important;
    box-shadow: none !important;
    background: white !important;
    visibility: visible !important;
  }
  
  #billContent *, #billContentWithoutGst * {
    visibility: visible !important;
  }
  
  /* Prevent page breaks inside critical elements */
  table {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  
  tr {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  
  /* Ensure consistent borders in print */
  table, th, td {
    border: 1px solid black !important;
    border-collapse: collapse !important;
  }
  
  @page {
    size: A4 portrait;
    margin: 5mm 5mm;
    margin-top: 10mm;
  }
}

/* Screen styles */
body {
  font-family: 'Times New Roman', serif;
  background: #f5f5f5;
  margin: 0;
  padding: 20px;
}

#billContent, #billContentWithoutGst {
  max-width: 750px;
  width: 100%;
  margin: 0 auto;
  background: white;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  box-sizing: border-box;
  font-size: 13px;
}

/* Header Section */
[style*="background-color: #f9f1b0"] {
  background-color: #f9f1b0 !important;
  border-bottom: 2px solid #d4b44a;
  padding: 8px 0 !important;
  margin-bottom: 10px;
}

[style*="color: #b01c1c"] {
  color: #b01c1c !important;
  font-weight: bold;
  font-size: 16px !important;
  margin: 5px 0 8px 0 !important;
  text-align: center;
}

/* Logo and Title Section */
[style*="display: flex; align-items: center; justify-content: space-around"] {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0 20px !important;
  margin-bottom: 5px;
}

[style*="height: 70px"] img {
  height: 60px !important;
  width: auto !important;
  border-radius: 30px !important;
  display: block;
}

/* Company Title */
[style*="SUPRIYA JEWELLERS"] h1 {
  font-size: 24px !important;
  font-weight: 900 !important;
  color: #c00 !important;
  margin: 0 !important;
  line-height: 1.2;
  letter-spacing: 0.5px;
}

[style*="Specialist in Gold"] h3 {
  font-size: 16px !important;
  font-weight: 600 !important;
  margin: 3px 0 0 0 !important;
  color: #333;
}

/* Contact Info */
[style*="flex: 1; display: flex; justify-content: flex-end"] {
  text-align: right;
  font-size: 12px !important;
  color: #333;
  margin-top: 5px;
}

/* Address */
[style*="font-size: 13px; color: #333; padding-top: 5px"] {
  font-size: 11px !important;
  color: #555 !important;
  padding-top: 3px !important;
  text-align: center;
  line-height: 1.3;
}

/* GST Number */
[style*="GST Number"] h3 {
  color: red !important;
  font-size: 14px !important;
  margin: 8px 0 5px 0 !important;
  text-align: center;
  font-weight: bold;
}

/* Horizontal Rules */
hr {
  border: none;
  height: 1px;
  background-color: #000;
  margin: 8px 0 !important;
}

/* Main Title */
h3[style*="text-align:center; margin:5px 0"] {
  text-align: center !important;
  margin: 8px 0 !important;
  font-size: 18px !important;
  font-weight: bold;
  text-transform: uppercase;
}

/* Customer Info Section */
[style*="display:flex; justify-content:space-between; font-size:14px"] {
  display: flex !important;
  justify-content: space-between !important;
  font-size: 12px !important;
  margin-bottom: 15px !important;
  gap: 20px;
}

[style*="display:flex; justify-content:space-between; font-size:14px"] p {
  margin: 4px 0 !important;
  line-height: 1.4;
}

[style*="display:flex; justify-content:space-between; font-size:14px"] strong {
  font-weight: bold;
  color: #333;
  display: inline-block;
}

/* Tables */
table {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 8px 0 !important;
  font-size: 12px !important;
}

table th {
  background-color: #f3f3f3 !important;
  font-weight: bold;
  padding: 6px 4px !important;
  text-align: center;
  border: 1px solid #000 !important;
  vertical-align: middle;
}

table td {
  padding: 4px !important;
  text-align: center;
  border: 1px solid #000 !important;
  vertical-align: middle;
}

/* Specific column alignments */
td[style*="text-align: left"] {
  text-align: left !important;
  padding-left: 6px !important;
}

td[style*="text-align: right"] {
  text-align: right !important;
  padding-right: 6px !important;
}

td[style*="text-align: center"] {
  text-align: center !important;
}

/* Amount in Words */
[style*="Amount Chargeable"] {
  margin: 12px 0 !important;
  padding-top: 8px !important;
  border-top: 1px solid #666 !important;
  font-size: 12px !important;
  line-height: 1.4;
}

[style*="Amount Chargeable"] strong {
  font-weight: bold;
  color: #333;
}

/* Payment Tables */
[style*="width: 100%; border-collapse: collapse; margin-top: 10px"] {
  margin-top: 12px !important;
  margin-bottom: 8px !important;
}

/* Terms and Conditions */
[style*="font-size:12px; margin-top:8px"] {
  font-size: 11px !important;
  margin-top: 10px !important;
  color: #555;
  line-height: 1.3;
}

/* Signature Section */
[style*="display:flex; justify-content:space-between; font-size:13px"] {
  display: flex !important;
  justify-content: space-between !important;
  font-size: 12px !important;
  margin-top: 20px !important;
  align-items: flex-end;
}

/* Watermark */
[style*="position: fixed; top: 50%; left: 50%"] {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  opacity: 0.2 !important;
  z-index: 9999 !important;
  pointer-events: none !important;
}

[style*="position: fixed; top: 50%; left: 50%"] img {
  width: 350px !important;
  height: 350px !important;
  object-fit: contain;
}

/* Bank Details Table */
[style*="line-height: 1.3"] table {
  font-size: 13px !important;
  line-height: 2 !important;
}

[style*="line-height: 1.3"] td {
  padding: 2px 4px !important;
  border: 2px !important;
}

/* Remove any unnecessary spacing */
br {
  margin: 0 !important;
  padding: 0 !important;
}

/* Ensure consistent text rendering */
* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* Print-specific improvements */
@media print {
  /* Ensure backgrounds print properly */
  [style*="background-color"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Prevent orphans and widows */
  p, td, th {
    orphans: 3;
    widows: 3;
  }
  
  /* Better table handling */
  table {
    font-size: 11px !important;
  }
  
  /* Adjust padding for print */
  #billContent, #billContentWithoutGst {
    padding: 5mm 8mm !important;
  }
}
`;